import { ClientLogger } from 'bernie-client';
import { ContextInput } from 'bernie-context';
import { QueryObject, SerializedData, timeoutPromise } from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import { ClientOptions, FetchPolicy } from 'bernie-graphql';
import { getErrorString, Logger, SystemEvent } from 'bernie-logger';
import { AnalyticsStore, ExperimentStore } from 'bernie-plugin-mobx';
import { action, computed, observable, runInAction } from 'mobx';
import { getTrackingInfoForLoggingFromHeaders } from '../common/logging-utils';
import {
  ActivityDestinationViewSearch,
  ActivityDetailBasic,
  ActivityDetailOffer,
  ActivityDetailOffers,
  ActivityDetailOffersWithAvailability,
  ActivityDetailReviews,
  ActivitySelectedValueInput
} from '../common/__generated__/api';
import {
  getDateRange,
  getRegionDetails,
  getStickyBarLeadPrice,
  trackEventWhenStickyBarPriceMismatches,
  trackInventoryErrorEvent
} from '../helpers/activity-detail-helper';
import { saveOfferFilters, trackOfferFilterEventOnPageLoad } from '../helpers/offer-group-helper';
import { DetailCriteriaStore, DetailQueryObject } from './detail-criteria-store';
import { LXStore, RequestWithTraceInfo } from './lx-store';
import { STORE_EVENTS } from './store-events';

export interface ActivityPreloadedData {
  activity: ActivityDestinationViewSearch.ActivityInlineFragment;
}

export class ActivityDetailStore extends LXStore {
  @observable public activityAvailability: ActivityDetailOffersWithAvailability.Availabilities[] = [];
  @observable public activityDetail: ActivityDetailBasic.ActivityInfo | null = null;
  @observable public activityOffers: ActivityDetailOffers.Offers[] = [];
  @observable public activityPreloadedData: ActivityPreloadedData | null = null;
  @observable public activityReviews: ActivityDetailReviews.Reviews[] = [];
  @observable public availabilityCalendar: ActivityDetailBasic.AvailabilityCalendar[] = [];
  @observable public isDetailFetchError?: boolean;
  @observable public isOfferBookButtonClicked = false;
  @observable public isOfferDialogOpen = false;
  @observable public isOffersLoading = true;
  @observable public showInclusions = true;
  @observable public uisPayload: any;
  @observable public photoGalleryIndex = 0;
  @observable public reviewBlocked = false;

  public detailUrl?: string;
  public offerInclusionFilter: ActivityDetailBasic.Filters | null = null;
  public query = {};
  public serverSide?: boolean;

  private analyticsStore: AnalyticsStore | null = null;
  private experimentStore: ExperimentStore | null = null;
  private isPageLoading?: boolean;
  private offerMealInclusions: ActivitySelectedValueInput[] = [];
  private offerFiltersMap: Map<string, ActivitySelectedValueInput> | null = null;
  private reviewsBeingFetched?: boolean;

  public constructor(
    bexConfigs: ClientOptions,
    logger: Logger | ClientLogger,
    appConfig: KeyedObject = {},
    requestOrContext?: ContextInput | RequestWithTraceInfo
  ) {
    super(bexConfigs, logger, appConfig, requestOrContext);
    this.logger = logger;
  }

  public hydrate(data: SerializedData): void {
    this.activityDetail = data.activityDetail;
    this.activityAvailability = data.activityAvailability;
    this.availabilityCalendar = data.availabilityCalendar;
    this.uisPayload = data.uisPayload;
    this.activityReviews = data.activityReviews;
    this.detailUrl = data.detailUrl;
    this.isOffersLoading = data.isOffersLoading;
    super.hydrate(data);
  }

  public isReviewsBeingFetched(): boolean | undefined {
    return this.reviewsBeingFetched;
  }

  public setReviewsBeingFetched(value: boolean): void {
    this.reviewsBeingFetched = value;
  }

  public resetActivityReviews(): void {
    this.activityReviews = [];
  }

  public resetActivityOffers(): void {
    this.activityOffers = [];
    this.isOffersLoading = true;
  }

  public resetDetailsPage(preloadedActivity?: ActivityDestinationViewSearch.ActivityInlineFragment): void {
    this.activityDetail = null;
    this.resetActivityOffers();
    this.resetActivityReviews();
    if (preloadedActivity) {
      this.activityPreloadedData = {
        activity: preloadedActivity
      };
    }
  }

  @action
  public fetchActivityDetail(
    detailVariables: ActivityDetailBasic.Variables,
    detailCriteriaStore: DetailCriteriaStore,
    pageIdentity: string,
    query: QueryObject
  ): Promise<any> {
    this.isDetailFetchError = false;

    return timeoutPromise(
      this.bexApiSource.operations.activityDetailBasic(detailVariables, {
        pageId: pageIdentity,
        fetchPolicy: FetchPolicy.NO_CACHE
      }),
      this.appConfig.timeout?.serverStoreFetch,
      `store=detail fetch=fetchActivityDetail timeout ${getTrackingInfoForLoggingFromHeaders(this.headers)}`
    )
      .then((response: KeyedObject) => {
        runInAction('update bex api activity detail from response', () => {
          if (response.data) {
            this.activityDetail = response.data.activityInfo;
            if (response.data.activityInfo.offers) {
              let leadPrice;
              this.isOffersLoading = false;
              this.activityOffers = response.data.activityInfo.offers;
              if (this.activityDetail) {
                this.activityDetail.offersSummary.filters = response.data.activityInfo.offersSummary.filters;
                leadPrice = getStickyBarLeadPrice(this.activityDetail.offersSummary.leadTicketView);
              }

              if (this.isPageLoading && leadPrice && this.analyticsStore) {
                trackEventWhenStickyBarPriceMismatches(this.activityOffers, leadPrice, this.analyticsStore);
                this.isPageLoading = false;
              }

              if (this.analyticsStore) {
                trackOfferFilterEventOnPageLoad(
                  this.analyticsStore,
                  this.activityOffers,
                  this.activityDetail ? this.activityDetail.offersSummary : undefined
                );
              }
            }

            this.processFetchNewDetailsResponse(response, detailCriteriaStore, query);
          } else {
            this.detiailFetchFailure(detailVariables);
          }
        });
      })
      .catch((error: Error) => {
        runInAction('recovering from bex api activity detail call breakdown', () => {
          this.detiailFetchFailure(detailVariables, error);
        });
      });
  }

  @action
  public fetchActivityOffersWithAvailability(
    detailVariables: ActivityDetailBasic.Variables,
    detailCriteriaStore: DetailCriteriaStore,
    pageIdentity: string,
    query: QueryObject
  ): Promise<any> {
    this.isDetailFetchError = false;

    return this.bexApiSource.operations
      .activityDetailOffersWithAvailability(detailVariables, {
        pageId: pageIdentity,
        fetchPolicy: FetchPolicy.NO_CACHE
      })
      .then((response: KeyedObject) => {
        runInAction('update bex api activity detail offers with availability from response', () => {
          if (this.activityDetail) {
            const leadPrice = getStickyBarLeadPrice(this.activityDetail.offersSummary.leadTicketView);

            this.isOffersLoading = false;
            this.activityOffers = response.data.activityInfo.offers;
            this.activityDetail.presentation.banners = response.data.activityInfo.presentation.banners;
            this.activityDetail.presentation.promotionMessage =
              response.data.activityInfo.presentation.promotionMessage;
            this.activityDetail.offersSummary = response.data.activityInfo.offersSummary;
            this.processFetchNewDetailsResponse(response, detailCriteriaStore, query);

            if (leadPrice && this.analyticsStore) {
              trackEventWhenStickyBarPriceMismatches(this.activityOffers, leadPrice, this.analyticsStore);
            }

            if (this.experimentStore && this.analyticsStore) {
              trackInventoryErrorEvent(
                this.experimentStore,
                this.activityDetail.presentation.banners,
                this.analyticsStore
              );

              trackOfferFilterEventOnPageLoad(
                this.analyticsStore,
                this.activityOffers,
                this.activityDetail.offersSummary
              );
            }
          }
        });
      })
      .catch((error: Error) => {
        runInAction('recovering from bex api activity detail availability call breakdown', () => {
          this.failureHandler(detailVariables, STORE_EVENTS.DETAIL_AVAILABILITY_FETCH_FAILED, error);
          this.isOffersLoading = false;
        });
      });
  }

  @action
  public fetchActivityReviews(
    variables: ActivityDetailReviews.Variables,
    resetReviews = false,
    pageIdentity: string
  ): Promise<any> {
    if (this.reviewBlocked) {
      return Promise.resolve();
    }

    return this.bexApiSource.operations
      .activityDetailReviews(variables, {
        pageId: pageIdentity,
        fetchPolicy: FetchPolicy.NO_CACHE,
      })
      .then((response: KeyedObject) => {
        runInAction('update bex api activity detail reviews from response', () => {
          if (resetReviews) {
            this.resetActivityReviews();
          }
          this.activityReviews = this.activityReviews.concat(response.data.activityInfo.reviews);
          this.setReviewsBeingFetched(false);
        });
      })
      .catch((error: Error) => {
        runInAction('recovering from bex api activity detail reviews call breakdown', () => {
          this.failureHandler(variables, STORE_EVENTS.DETAIL_REVIEWS_FETCH_FAILED, error);
        });
      });
  }

  @action
  public fetchActivityOffers(variables: ActivityDetailOffers.Variables, pageIdentity: string): Promise<any> {
    return this.bexApiSource.operations
      .activityDetailOffers(variables, {
        pageId: pageIdentity,
        fetchPolicy: FetchPolicy.NO_CACHE,
      })
      .then((response: KeyedObject) => {
        runInAction('update bex api activity detail offers from response', () => {
          let leadPrice;
          this.isOffersLoading = false;
          this.activityOffers = response.data.activityInfo.offers;

          if (this.activityDetail) {
            this.activityDetail.offersSummary.filters = response.data.activityInfo.offersSummary.filters;
            leadPrice = getStickyBarLeadPrice(this.activityDetail.offersSummary.leadTicketView);
          }

          this.offerFiltersMap = saveOfferFilters(this.offerFiltersMap, this.activityOffers);

          if (this.isPageLoading && leadPrice && this.analyticsStore) {
            trackEventWhenStickyBarPriceMismatches(this.activityOffers, leadPrice, this.analyticsStore);
            this.isPageLoading = false;
          }

          if (this.analyticsStore) {
            trackOfferFilterEventOnPageLoad(
              this.analyticsStore,
              this.activityOffers,
              this.activityDetail ? this.activityDetail.offersSummary : undefined
            );
          }
        });
      })
      .catch((error: Error) => {
        runInAction('recovering from bex api activity detail offers call breakdown', () => {
          this.failureHandler(variables, STORE_EVENTS.DETAIL_OFFERS_FETCH_FAILED, error);
          this.isOffersLoading = false;
        });
      });
  }

  @action
  public fetchOffer(variables: ActivityDetailOffer.Variables, pageIdentity: string, offerIndex: number): Promise<any> {
    return this.bexApiSource.operations
      .activityDetailOffer(variables, {
        pageId: pageIdentity,
        fetchPolicy: FetchPolicy.NO_CACHE
      })
      .then((response: KeyedObject) => {
        runInAction('update bex api activity detail offer from response', () => {
          this.activityOffers[offerIndex] = response.data.activityInfo.offer;
          this.offerFiltersMap = saveOfferFilters(this.offerFiltersMap, [response.data.activityInfo.offer]);
        });
      })
      .catch((error: Error) => {
        runInAction('Error while getting offer detail.', () => {
          this.failureHandler(variables, STORE_EVENTS.DETAIL_OFFER_FETCH_FAILED, error);
        });
      });
  }

  public failureHandler(variable: KeyedObject | null, event: SystemEvent, error?: Error): void {
    this.logger.logEvent(event, variable, error ? getErrorString(error) : null);
  }

  public populateFirstOfferAvailableDate(detailCriteriaStore: DetailCriteriaStore): void {
    if (this.activityAvailability && this.activityAvailability.length > 0) {
      const filtered = this.activityAvailability.filter((t) => t.isAvailable);
      detailCriteriaStore.selectedDate = filtered.length > 0 ? filtered[0].date : null;
    }
  }

  public isOfferAvailabilityPresent(): boolean {
    if (this.activityAvailability && this.activityAvailability.length > 0) {
      const filtered = this.activityAvailability.filter((t) => t.isAvailable);

      return filtered.length > 0;
    }

    return false;
  }

  public isReviewsPresent(): boolean {
    if (!this.activityDetail || !this.activityDetail.activity.reviewSummary) {
      return false;
    }

    return parseInt(this.activityDetail.activity.reviewSummary.total, 10) > 0;
  }

  public getReviewSource(): string | null {
    return this.activityDetail?.activity?.reviewSummary?.source || null;
  }

  public isExternalReview(): boolean {
    return this.activityDetail?.activity?.reviewSummary?.blockWebCrawlersEnabled || false;
  }

  public setAnalyticsStore(analyticsStore: AnalyticsStore): void {
    this.analyticsStore = analyticsStore;
  }

  public setExperimentStore(experimentStore: ExperimentStore): void {
    this.experimentStore = experimentStore;
  }

  public getExperimentStore(): ExperimentStore | null {
    return this.experimentStore;
  }

  public setPageLoading(isPageLoading: boolean): void {
    this.isPageLoading = isPageLoading;
  }

  public getOfferFiltersMap(): Map<string, ActivitySelectedValueInput> | null {
    return this.offerFiltersMap;
  }

  public setOfferFiltersMap(offerFiltersMap: Map<string, ActivitySelectedValueInput>): void {
    this.offerFiltersMap = offerFiltersMap;
  }

  public getOfferMealInclusions(): ActivitySelectedValueInput[] {
    return this.offerMealInclusions;
  }

  public setOfferMealInclusions(offerMealInclusions: ActivitySelectedValueInput[]): void {
    this.offerMealInclusions = offerMealInclusions;
  }

  public setOfferFilters(filters: ActivityDetailBasic.Filters[]): void {
    if (this.activityDetail) {
      this.activityDetail.offersSummary.filters = filters;
    }
  }

  @computed
  public get datePickerContent(): ActivityDetailBasic.AvailabilityCalendar[] | KeyedObject | null {
    return this.availabilityCalendar
      ? this.availabilityCalendar.reduce((content: KeyedObject, availability) => {
          content[availability.date.isoFormat] = availability.price && availability.price.formatted;

          return content;
        }, {})
      : null;
  }

  @computed
  public get activityReviewsForReviewSection(): ActivityDetailReviews.Reviews[] {
    return this.activityReviews.length > 0 ? this.activityReviews.slice(0, 5) : [];
  }

  private processFetchNewDetailsResponse(
    response: KeyedObject,
    detailCriteriaStore: DetailCriteriaStore,
    query: QueryObject
  ): void {
    this.activityAvailability = response.data.activityInfo && response.data.activityInfo.availabilities;
    this.availabilityCalendar = response.data.activityInfo.availabilityCalendar;
    this.uisPayload = response.extensions.analytics;
    this.updateDetailCriteria(detailCriteriaStore, query);
    this.populateFirstOfferAvailableDate(detailCriteriaStore);
    detailCriteriaStore.setQueryStringUpdated(false);
    this.activityPreloadedData = null;
  }

  private updateDetailCriteria(detailCriteriaStore: DetailCriteriaStore, query: QueryObject): void {
    const regionDetails = getRegionDetails(this, query);
    let newParams: DetailQueryObject = {} as DetailQueryObject;
    if (regionDetails) {
      newParams = {
        ...newParams,
        location: regionDetails.location,
        rid: regionDetails.rid
      };
    }

    const availabilities = this.activityAvailability;
    const dateRange = getDateRange(availabilities);

    if (dateRange) {
      newParams = {
        ...newParams,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
    }

    detailCriteriaStore.detailQuery = {
      ...detailCriteriaStore.detailQuery,
      ...newParams
    };
  }

  private detiailFetchFailure(detailVariables: ActivityDetailBasic.Variables, error?: Error): void {
    this.isDetailFetchError = true;
    this.failureHandler(detailVariables, STORE_EVENTS.DETAIL_BASIC_FETCH_FAILED, error);
  }
}
